import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Collapse from '@material-ui/core/Collapse'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Typography from '@material-ui/core/Typography'
import FormControl from '@material-ui/core/FormControl'
import FormGroup from '@material-ui/core/FormGroup'
import FormLabel from '@material-ui/core/FormLabel'
import Grid from '@material-ui/core/Grid'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import ProviderAPI from 'config/api/ProviderAPI'
// AdminAPI import removed — no longer needed for auto plan-type switching
import { onError } from 'config/lib/errorLib'
import CountySelect from 'components/general/CountySelect'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import { NotificationManager } from 'react-notifications'
import Loader from 'components/general/Loader'
import CountiesMap from './CountiesMap'
import EircodeAreasMap from './EircodeAreasMap'
import MissingInfoDialog from './MissingInfoDialog'

const useStyles = makeStyles(componentStyles)

function CountiesCard(props) {
    const classes = { ...useStyles() }
    const [open, setOpen] = useState(true)
    const api = new ProviderAPI()

    const company = props.companyDetails || {}

    // Multi-product tabbed mode when a products array is supplied; otherwise
    // single-product (props.product) or legacy global coverage.
    const products = (props.products && props.products.length) ? props.products : null
    const [selectedProduct, setSelectedProduct] = useState(products ? products[0] : (props.product || null))
    const activeProduct = products ? selectedProduct : (props.product || null)

    // When a product is active, coverage is stored per product under
    // company.coverage[product]; otherwise it uses the legacy top-level fields.
    const basePath = activeProduct ? `coverage.${activeProduct}.` : ''

    // Resolve the coverage values for a product (tabbed mode) or the values
    // passed via props (single-product / legacy global mode). Per-product
    // coverage falls back to the legacy global coverage only until the product
    // has its own coverage entry; once the entry exists we use it as-is (even
    // when empty) so the user can clear/uncheck areas.
    function resolveCoverage(prod) {
        if (products && prod) {
            const c = company.coverage && company.coverage[prod]
            if (c) {
                return { coverageType: c.coverageType, counties: c.counties || [], eircodeAreas: c.eircodeAreas || [] }
            }
            return { coverageType: company.coverageType, counties: company.counties || [], eircodeAreas: company.eircodeAreas || [] }
        }
        return { coverageType: props.coverageType, counties: props.counties || [], eircodeAreas: props.eircodeAreas || [] }
    }

    const resolved = resolveCoverage(activeProduct)
    const counties = resolved.counties
    const eircodeAreas = resolved.eircodeAreas

    // coverageType: 'counties' | 'eircodes'
    const [coverageType, setCoverageType] = useState(resolved.coverageType || 'counties')

    function handleProductChange(prod) {
        setSelectedProduct(prod)
        const r = resolveCoverage(prod)
        setCoverageType(r.coverageType || 'counties')
    }

    // Keep the coverage type stored per product so a single Save can persist
    // every product's coverage, not just the active one.
    function handleCoverageTypeChange(val) {
        setCoverageType(val)
        if (products && activeProduct) {
            props.updateField(`coverage.${activeProduct}.coverageType`, val)
        }
    }

    const [loading, setLoading] = useState(false)
    const [showMissingFields, setShowMissingFields] = useState(false)
    const [missingFields, setMissingFields] = useState([])

    function getMissingFields() {
        // New logic: require at least one county OR one eircode area regardless of mode
        if ((!counties || counties.length === 0) && (!eircodeAreas || eircodeAreas.length === 0)) {
            return [{ key: 'coverage', label: 'Select at least one county or draw at least one eircode area' }]
        }
        return []
    }

    async function saveDetails() {
        const missing = getMissingFields()
        if (missing.length > 0) {
            setMissingFields(missing)
            setShowMissingFields(true)
            return
        }
        setLoading(true)
        try {
            // Effective coverage type: prefer eircode if user chose it and provided areas; else counties
            const effectiveCoverageType = (coverageType === 'eircodes' && eircodeAreas.length > 0) ? 'eircodes' : 'counties'
            let company
            if (products) {
                // Save ALL products' coverage in one write, not just the active one.
                const source = (props.companyDetails && props.companyDetails.coverage) || {}
                const fullCoverage = {}
                Object.keys(source).forEach(prod => {
                    const c = source[prod] || {}
                    const cCounties = c.counties || []
                    const cEircodes = c.eircodeAreas || []
                    const effType = (c.coverageType === 'eircodes' && cEircodes.length > 0) ? 'eircodes' : 'counties'
                    fullCoverage[prod] = { coverageType: effType, counties: cCounties, eircodeAreas: cEircodes }
                })
                // Ensure the currently-viewed product reflects the latest selection.
                if (activeProduct) {
                    fullCoverage[activeProduct] = { coverageType: effectiveCoverageType, counties, eircodeAreas }
                }
                company = await api.updateAllCoverage(props.companyId, fullCoverage)
            } else if (activeProduct) {
                company = await api.updateProductCoverage(
                    props.companyId,
                    activeProduct,
                    effectiveCoverageType,
                    counties,
                    eircodeAreas
                )
            } else {
                company = await api.updateCoverage(
                    props.companyId,
                    effectiveCoverageType,
                    counties,
                    eircodeAreas
                )
            }

            props.updateCompany && props.updateCompany(company)
            //props.updateField && props.updateField('eircodeAreas', eircodeAreas)
            props.updateStage && props.updateStage('technology')
            NotificationManager.success('Coverage saved')
        } catch (e) {
            onError(e)
        }
        setLoading(false)
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <>
                        <Box display='flex' style={{ justifyContent: 'space-between' }}>
                            <Box>
                                <Box component={Typography} variant='h2' marginBottom='0!important'>
                                    <Box component='span'>Coverage</Box>
                                </Box>
                                <Box
                                    component='p'
                                    fontSize='.875rem'
                                    marginBottom='0'
                                    marginTop='0'
                                    lineHeight='1.7'
                                    fontWeight='300'
                                >
                                    {products ? 'Select a product, then choose the areas you serve for it' : 'Please select the areas that you serve'}
                                </Box>
                            </Box>
                        </Box>
                    </>
                }
                onClick={() => setOpen(o => !o)}
                style={{ cursor: 'pointer' }}
                action={<ExpandMoreIcon style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginTop: 4 }} />}
                classes={{ root: classes.cardHeaderRoot }}
            ></CardHeader>
            <Collapse in={open}>
            <CardContent>
                <Grid container spacing={2}>
                    {products && (
                        <Grid item xs={12}>
                            <FormGroup>
                                <FormLabel>Product</FormLabel>
                                <FormControl variant='filled' component={Box} width='25%'>
                                    <Select
                                        value={selectedProduct}
                                        onChange={(e) => handleProductChange(e.target.value)}
                                        input={<OutlinedInput />}
                                    >
                                        {products.map(p => (
                                            <MenuItem key={p} value={p}>{p}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </FormGroup>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Box>
                            <Tabs
                                value={coverageType}
                                onChange={(e, val) => handleCoverageTypeChange(val)}
                                indicatorColor='primary'
                                textColor='primary'
                                variant='fullWidth'
                            >
                                <Tab label='Counties' value='counties' />
                                <Tab label='Eircodes (Select on Map)' value='eircodes' />
                            </Tabs>
                        </Box>
                        {coverageType === 'eircodes' && (
                            <Box mt={2} p={2} style={{ backgroundColor: '#e3f2fd', borderRadius: 4, border: '1px solid #90caf9' }}>
                                <Typography variant='body2' style={{ color: '#1565c0' }}>
                                    Eircode coverage has a higher price per lead than county coverage due to more precise targeting.
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        {coverageType === 'counties' && (
                            <FormGroup>
                                <FormLabel>
                                    Counties
                                </FormLabel>
                                <FormControl variant='filled' component={Box} width='25%'>
                                    <CountySelect
                                        counties={counties}
                                        updateCounties={(val) => props.updateField(`${basePath}counties`, val)}
                                        allOption={true}
                                        multiple={true}
                                    />
                                </FormControl>
                            </FormGroup>
                        )}
                        {coverageType === 'eircodes' && (
                            <FormGroup>
                                <FormLabel>
                                    Eircode Areas
                                </FormLabel>
                                <Box component={Typography} variant='body2' color='textSecondary' mt={1} style={{ maxWidth: 700, lineHeight: 1.5 }}>
                                    Draw one or more circular areas on the map. Leads whose Eircodes fall inside any of these areas will be routed to you. 
                                </Box>
                            </FormGroup>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        {coverageType === 'counties' && <CountiesMap counties={counties} />}
                        {coverageType === 'eircodes' && (
                            <Box>
                                <EircodeAreasMap
                                    areas={eircodeAreas}
                                    updateAreas={(areas) => props.updateField(`${basePath}eircodeAreas`, areas)}
                                />
                            </Box>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Box display='flex' justifyContent='flex-end' width='100%'>
                            <Button
                                variant='contained'
                                color='primary'
                                disabled={loading}
                                onClick={saveDetails}
                            >
                                {!loading ? <>{(props.admin || props.profile) ? 'Save' : 'Save & Continue'}</> : <Loader height={12} />}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
            </Collapse>
            <MissingInfoDialog
                open={showMissingFields}
                onClose={() => setShowMissingFields(false)}
                missingFields={missingFields}
            />
        </Card>
    )
}

export default CountiesCard